import React, { Component, Fragment } from 'react'

import * as THREE from 'three'
import DomEvents from '../../../utils/domevents'
import text from 'public/assets/png/hotspots/fill_circle.png'
import photo from 'public/assets/png/hotspots/photo_hotspot.png'
import video from 'public/assets/png/hotspots/video_hotspot.png'
import panorama from 'public/assets/png/hotspots/360_hotspot.png'
import CHECK_CIRCLE from 'public/assets/png/check_circle.png'

import { Sprite3D } from '../sprite3D/'
import { HitCircle3D } from '../hitMesh3D'
import { dispose3DObject } from '../../../utils'

const iconNames = {
    text,
    photo,
    video,
    panorama,
}

const HOTSPOT_RADIUS = 0.25

class Hotspot3D extends Component {
    constructor(props) {
        super(props)
        this.domEvents
        this.rootObj
        this.mixer = {
            torus: null,
            sprite: null,
            mesh: null,
        }
        this.actions = {
            torus: {},
            sprite: {},
            mesh: {},
        }
        this.clock = new THREE.Clock()
    }

    state = {
        meshScale: {
            x: 0.5,
            y: 0.5,
            z: 0.5,
        },
        spriteScale: {
            x: 0.115,
            y: 0.09,
            z: 0.09,
        },
    }

    valBetween(v, min, max) {
        return Math.min(max, Math.max(min, v))
    }

    capObjectValues(obj, min, max) {
        let newObj = Object.assign({}, obj)
        Object.keys(newObj).forEach(key => {
            newObj[key] = this.valBetween(newObj[key], min, max)
        })
        return newObj
    }

    componentWillMount() {
        const { scene, camera, renderer, meshPosition, fov = 100 } = this.props

        this.rootObj = new THREE.Object3D()
        let cappedMeshPosition = this.capObjectValues(meshPosition, -1.5, 1.5)
        let normalizedFOVScaleOffset = fov / 100

        this.rootObj.position.set(cappedMeshPosition.x, cappedMeshPosition.y, cappedMeshPosition.z)

        this.rootObj.scale.set(
            this.rootObj.scale.x * normalizedFOVScaleOffset,
            this.rootObj.scale.y * normalizedFOVScaleOffset,
            this.rootObj.scale.z * normalizedFOVScaleOffset
        )
        this.domEvents = new DomEvents(camera, renderer.domElement)
        this.mountMesh()
        this.mountSprite()
        this.mountTorus()

        scene.add(this.rootObj)
    }

    componentDidUpdate(prevProps) {
        const { currentState } = this.props

        // Toggle between panning and default animation based on panning state
        // Use of currentlyPanning boolean prevents throttling of animation on update
        if (currentState === 'panning' && prevProps.currentState !== 'panning') {
            this.setPanningAnimation()
        } else if (currentState === 'default' && prevProps.currentState !== 'default') {
            this.setDefaultAnimation()
        } else if (currentState === 'visited' && prevProps.currentState !== 'visited') {
            this.setVisitedState()
        }
    }

    mountMesh = () => {
        // https://github.com/mrdoob/three.js/blob/master/examples/misc_animation_keys.html
        // https://stackoverflow.com/questions/42383681/verify-when-a-clipaction-animation-finished-playing-in-three-js
        const { meshScale } = this.state
        const obj = new THREE.Mesh(
            new THREE.CircleBufferGeometry(HOTSPOT_RADIUS, 20),
            new THREE.MeshBasicMaterial({ color: 0x000000, opacity: 0.5, transparent: true })
        )
        obj.material.transparent = true

        if (meshScale) obj.scale.set(meshScale.x, meshScale.y, meshScale.z)

        this.rootObj.add(obj)

        this.mixer.mesh = new THREE.AnimationMixer(obj)

        var decreaseOpacityKF = new THREE.VectorKeyframeTrack('.material.opacity', [0, 1], [1, 0])
        var visitedClip = new THREE.AnimationClip('visited', 1, [decreaseOpacityKF])
        this.actions.mesh.visitedClip = this.mixer.mesh.clipAction(visitedClip)
        this.actions.mesh.visitedClip.loop = THREE.LoopOnce
        this.actions.mesh.visitedClip.clampWhenFinished = true

        this.animate()
    }

    mountSprite = () => {
        const { type } = this.props
        const { spriteScale } = this.state

        // MESH SETUP
        var spriteMap = new THREE.TextureLoader().load(iconNames[type])
        var spriteMaterial = new THREE.SpriteMaterial({ map: spriteMap, color: 0xffffff })
        var obj = new THREE.Sprite(spriteMaterial)

        // Adds a slight offset to avoid z-fighting.  Need to investigate why this is required
        obj.position.set(0.001, 0, 0.001)

        if (spriteScale) obj.scale.set(spriteScale.x, spriteScale.y, spriteScale.z)
        this.rootObj.add(obj)

        // ANIMATIONS: Create animations

        // Create Mixer
        this.mixer.sprite = new THREE.AnimationMixer(obj)

        // Create keyframes
        var opacityFadeOutKF = new THREE.VectorKeyframeTrack('.material.opacity', [0, 0.5, 1], [1, 0.85, 0.5])
        var defaultClip = new THREE.AnimationClip('default', 1, [opacityFadeOutKF])
        // setup the AnimationMixer
        // create a ClipAction and set it to play
        this.actions.sprite.defaultClip = this.mixer.sprite.clipAction(defaultClip)
        this.actions.sprite.defaultClip.loop = THREE.LoopRepeat

        var decreaseOpacityKF = new THREE.VectorKeyframeTrack('.material.opacity', [0, 1], [1, 0])
        var visitedClip = new THREE.AnimationClip('visited', 1, [decreaseOpacityKF])
        this.actions.sprite.visitedClip = this.mixer.sprite.clipAction(visitedClip)
        this.actions.sprite.visitedClip.loop = THREE.LoopOnce
        this.actions.sprite.visitedClip.clampWhenFinished = true

        this.actions.sprite.defaultClip.play()
    }

    mountTorus = () => {
        const { meshScale } = this.state

        // MESH SETUP
        const obj = new THREE.Mesh(
            new THREE.TorusBufferGeometry(0.25, 0.01, 10, 64),
            new THREE.MeshBasicMaterial({ color: 0xffffff, opacity: 0.5, transparent: true })
        )
        obj.material.transparent = true
        obj.material.opacity = 1

        // set initial values to prevent flash distorted geometry before animation kicks in
        if (meshScale) obj.scale.set(meshScale.x, meshScale.y, meshScale.z)

        this.rootObj.add(obj)

        // ANIMATIONS: Create animations

        // Create Mixer
        this.mixer.torus = new THREE.AnimationMixer(obj)

        // Create keyframes
        var scaleKF = new THREE.VectorKeyframeTrack('.scale', [0, 0.5, 1], [0.5, 0.5, 0.5, 0.7, 0.7, 0.7, 1, 1, 1])
        var opacityFadeOutKF = new THREE.VectorKeyframeTrack('.material.opacity', [0, 0.5, 1], [1, 0.7, 0])
        var defaultClip = new THREE.AnimationClip('default', 1, [scaleKF, opacityFadeOutKF])

        // create a ClipAction and set it to play
        this.actions.torus.defaultClip = this.mixer.torus.clipAction(defaultClip)
        this.actions.torus.defaultClip.loop = THREE.LoopRepeat

        var decreaseOpacityKF = new THREE.VectorKeyframeTrack('.material.opacity', [0, 1], [1, 0])
        var visitedClip = new THREE.AnimationClip('visited', 1, [decreaseOpacityKF])
        this.actions.torus.visitedClip = this.mixer.torus.clipAction(visitedClip)
        this.actions.torus.visitedClip.loop = THREE.LoopOnce
        this.actions.torus.visitedClip.clampWhenFinished = true

        this.actions.torus.defaultClip.play()
    }

    setPanningAnimation = () => {
        // Note: Would be easier from a structural perspective to just make a new animation
        // However, the shrinking effect requires an immediate reversal of the movement from the current frame
        // Easiest way to do this is to reverse direction of presently playing animation

        // Reverses animation timescale to shrink scale and restore opacity
        this.actions.torus.defaultClip.timeScale = -2
        this.actions.torus.defaultClip.setLoop(THREE.LoopOnce)
        // Pauses animation when finished to preserve ring around sprite
        this.actions.torus.defaultClip.clampWhenFinished = true
        this.actions.torus.defaultClip.play()

        this.actions.sprite.defaultClip.setLoop(THREE.LoopOnce)
        this.actions.sprite.defaultClip.timeScale = -2
        // // Pauses animation when finished to preserve ring around sprite
        this.actions.sprite.defaultClip.clampWhenFinished = true
        this.actions.sprite.defaultClip.play()

        // this.actions.sprite.panningClip.play()
    }

    setDefaultAnimation = () => {
        // Sets animation back to original behavior from panning reverseal
        // https://gist.github.com/rtpHarry/2d41811d04825935039dfc075116d0ad
        this.actions.torus.defaultClip.stop()
        this.actions.torus.defaultClip.timeScale = 1
        this.actions.torus.defaultClip.setLoop(THREE.LoopRepeat)
        this.actions.torus.defaultClip.clampWhenFinished = false
        this.actions.torus.defaultClip.play()

        this.actions.sprite.defaultClip.stop()
        this.actions.sprite.defaultClip.timeScale = 1
        this.actions.sprite.defaultClip.setLoop(THREE.LoopRepeat)
        this.actions.sprite.defaultClip.clampWhenFinished = false
        this.actions.sprite.defaultClip.play()

        // this.actions.sprite.panningClip.stop()
        // this.actions.sprite.defaultClip.timeScale = -1
        // this.actions.sprite.defaultClip.play()
    }

    setVisitedState = () => {
        Object.keys(this.actions).forEach(key => {
            this.actions[key] && this.actions[key].defaultClip && this.actions[key].defaultClip.stop()
            this.actions[key] && this.actions[key].visitedClip && this.actions[key].visitedClip.play()
        })
    }

    animate = _ => {
        const { camera } = this.props
        // Base animation to keep rootmesh rotate towards camera.
        // May want to refactor where it's invoked
        this.rootObj.quaternion.copy(camera.quaternion)

        var delta = this.clock.getDelta()
        Object.keys(this.mixer).forEach(key => {
            this.mixer[key] && this.mixer[key].update(delta)
        })

        this.rafMesh = requestAnimationFrame(this.animate)
    }

    componentWillUnmount() {
        const { scene } = this.props

        this.domEvents && this.domEvents.destroy()
        dispose3DObject(this.rootObj)
        scene.remove(this.rootObj)
        this.rafMesh && cancelAnimationFrame(this.rafMesh)
    }

    render() {
        const { scene, camera, renderer, currentState, onClick, onMouseEnter, isMobile } = this.props
        return (
            <Fragment>
                {currentState === 'visited' && (
                    <Sprite3D
                        rootObj={this.rootObj}
                        scene={scene}
                        camera={camera}
                        renderer={renderer}
                        containerWidth={0.25}
                        containerHeight={0.1}
                        isVisible={currentState === 'visited'}
                        spriteResource={CHECK_CIRCLE}
                        meshPosition={{
                            x: 0.13,
                            y: 0.1,
                            z: 0.02,
                        }}
                        scaleCofactor={{
                            x: 0.5,
                            y: 0.225,
                            z: 0.25,
                        }}
                    />
                )}
                {this.rootObj && (
                    <HitCircle3D
                        rootObj={this.rootObj}
                        radius={HOTSPOT_RADIUS * 1.1}
                        handleTouchStart={onClick}
                        handleClick={onClick}
                        handleMouseOver={onMouseEnter}
                        camera={camera}
                        renderer={renderer}
                        isMobile={isMobile}
                    />
                )}
            </Fragment>
        )
    }
}

Hotspot3D.defaultProps = {
    meshPosition: {
        x: -20,
        y: 0,
        z: 0,
    },
    onClick: data => {
        console.log('clicked Hotspot3D')
        console.log(data)
    },
    onHover: data => {
        console.log('hover Hotspot3D')
        console.log(data)
    },
    onExit: data => {
        console.log('exit Hotspot3D')
        console.log(data)
    },
}

export default Hotspot3D
